"use client"

import type { TranslateFn } from '@open-mercato/shared/lib/i18n/context'
import type { ColumnDef } from '@tanstack/react-table'
import type { FilterDef } from '@open-mercato/ui/backend/FilterBar'
import { DRIVER_QUALIFICATIONS, LICENSE_CATEGORIES } from './lookups'

export type DriverTranslate = TranslateFn

export type ExpiryStatus = 'valid' | 'warning' | 'danger' | 'expired' | 'missing'

export type DriverDetail = {
  id: string
  firstName: string
  lastName: string
  phone: string | null
  email: string | null
  pesel: string | null
  licenseCategories: string[]
  licenseNumber: string | null
  licenseIssuedBy: string | null
  licenseExpires: string | null
  licenseExpiresStatus: ExpiryStatus
  licenseExpiresDaysUntil: number | null
  qualifications: string[]
  adrCertificateNumber: string | null
  adrCertificateClasses: string | null
  adrExpires: string | null
  adrExpiresStatus: ExpiryStatus
  adrExpiresDaysUntil: number | null
  digitalTachographId: string | null
  driverCardExpires: string | null
  driverCardExpiresStatus: ExpiryStatus
  driverCardExpiresDaysUntil: number | null
  cpcNumber: string | null
  cpcExpires: string | null
  cpcExpiresStatus: ExpiryStatus
  cpcExpiresDaysUntil: number | null
  userId: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export type DriverListItem = {
  id: string
  firstName: string
  lastName: string
  phone: string | null
  licenseCategories: string[]
  licenseExpires: string | null
  qualifications: string[]
  adrExpires: string | null
  cpcExpires: string | null
  driverCardExpires: string | null
  isActive: boolean
}

export type DriversListResponse = {
  items: DriverListItem[]
  page: number
  pageSize: number
  total: number
  totalPages: number
}

function formatExpiryDate(value: string | null): string {
  if (!value) return '—'
  return value
}

export function buildDriversColumns(
  t: DriverTranslate,
  callbacks: {
    onToggleActive: (row: DriverListItem, next: boolean) => void
  },
): ColumnDef<DriverListItem>[] {
  return [
    {
      id: 'lastName',
      accessorFn: (row) => `${row.lastName} ${row.firstName}`,
      header: t('drivers.table.columns.name', 'Name'),
      enableSorting: true,
      meta: { editable: false },
    },
    {
      id: 'phone',
      accessorKey: 'phone',
      header: t('drivers.table.columns.phone', 'Phone'),
      enableSorting: false,
      meta: { editable: true, type: 'text' },
      cell: ({ getValue }) => (getValue() as string | null) ?? '—',
    },
    {
      id: 'licenseCategories',
      accessorKey: 'licenseCategories',
      header: t('drivers.table.columns.licenseCategories', 'License'),
      enableSorting: false,
      meta: {
        editable: true,
        type: 'multiselect',
        options: LICENSE_CATEGORIES.map((c) => ({ value: c, label: c })),
      },
      cell: ({ getValue }) => {
        const cats = getValue() as string[]
        return cats.length > 0 ? cats.join(', ') : '—'
      },
    },
    {
      id: 'licenseExpires',
      accessorKey: 'licenseExpires',
      header: t('drivers.table.columns.licenseExpires', 'License exp.'),
      enableSorting: true,
      meta: { editable: true, type: 'date' },
      cell: ({ getValue }) => formatExpiryDate(getValue() as string | null),
    },
    {
      id: 'qualifications',
      accessorKey: 'qualifications',
      header: t('drivers.table.columns.qualifications', 'Qualifications'),
      enableSorting: false,
      meta: {
        editable: true,
        type: 'multiselect',
        options: DRIVER_QUALIFICATIONS.map((q) => ({
          value: q,
          label: t(`drivers.lookups.qualification.${q}`, q),
        })),
      },
      cell: ({ getValue }) => {
        const quals = getValue() as string[]
        return quals.length > 0 ? quals.join(', ') : '—'
      },
    },
    {
      id: 'adrExpires',
      accessorKey: 'adrExpires',
      header: t('drivers.table.columns.adrExpires', 'ADR exp.'),
      enableSorting: true,
      meta: { editable: true, type: 'date' },
      cell: ({ getValue }) => formatExpiryDate(getValue() as string | null),
    },
    {
      id: 'cpcExpires',
      accessorKey: 'cpcExpires',
      header: t('drivers.table.columns.cpcExpires', 'CPC exp.'),
      enableSorting: true,
      meta: { editable: true, type: 'date' },
      cell: ({ getValue }) => formatExpiryDate(getValue() as string | null),
    },
    {
      id: 'driverCardExpires',
      accessorKey: 'driverCardExpires',
      header: t('drivers.table.columns.driverCardExpires', 'Card exp.'),
      enableSorting: true,
      meta: { editable: true, type: 'date' },
      cell: ({ getValue }) => formatExpiryDate(getValue() as string | null),
    },
    {
      id: 'isActive',
      accessorKey: 'isActive',
      header: t('drivers.table.columns.isActive', 'Active'),
      enableSorting: true,
      meta: { editable: false },
      cell: ({ getValue, row }) => {
        const active = getValue() as boolean
        return (
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => callbacks.onToggleActive(row.original, e.target.checked)}
            className="h-4 w-4 cursor-pointer"
          />
        )
      },
    },
  ]
}

export function buildDriversFilters(t: DriverTranslate): FilterDef[] {
  return [
    {
      id: 'qualification',
      label: t('drivers.table.filters.qualification', 'Qualification'),
      type: 'select',
      options: DRIVER_QUALIFICATIONS.map((q) => ({
        value: q,
        label: t(`drivers.lookups.qualification.${q}`, q),
      })),
      placeholder: t('drivers.table.filters.qualificationPlaceholder', 'Filter by qualification'),
    },
    {
      id: 'licenseCategory',
      label: t('drivers.table.filters.licenseCategory', 'License category'),
      type: 'select',
      options: LICENSE_CATEGORIES.map((c) => ({ value: c, label: c })),
    },
    {
      id: 'isActive',
      label: t('drivers.table.filters.active', 'Active'),
      type: 'checkbox',
    },
  ]
}
